import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Alert, Button, Select, Space, Typography } from 'antd';
import React from 'react';
import { DropdownOption } from '../../../core/models/DropdownOption';
import { errorWithUserMessage, success } from '../../../helpers/NotificationHelper';
import { filterOptionForSearch } from '../../../helpers/normalizeForSearch';
import { transformEnumToDropdownOptions } from '../../../helpers/TransformHelper';
import {
    CreateMatchOfficialCommandOfMatchOfficial,
    CreateOrUpdateMatchOfficialsCommand,
    MatchDetailVm,
    MatchOfficialVm,
    MatchesClient,
    OfficialRoleEnum,
    OfficialVm,
    OfficialsClient,
} from '../../../utils/api';

const { Text } = Typography;

/** Default slots: 2 referees, delegate, secretary, timekeeper, VAR */
const PRESET_ROLES: OfficialRoleEnum[] = [
    OfficialRoleEnum.Referee,
    OfficialRoleEnum.Referee,
    OfficialRoleEnum.Delegate,
    OfficialRoleEnum.Secretary,
    OfficialRoleEnum.Timekeeper,
    OfficialRoleEnum.VAR,
];

export interface OfficialPresetRow {
    key: string;
    roleId: OfficialRoleEnum;
    officialId?: number;
}

let rowKeySeq = 0;
const nextRowKey = (): string => `mo-preset-${++rowKeySeq}`;

function buildRowsFromMatch(match: MatchDetailVm): OfficialPresetRow[] {
    const pool: MatchOfficialVm[] = [...(match.officials || [])];
    const rows: OfficialPresetRow[] = [];

    for (const role of PRESET_ROLES) {
        const idx = pool.findIndex((o: MatchOfficialVm): boolean => o.roleId === role);
        if (idx >= 0) {
            const mo = pool.splice(idx, 1)[0];
            rows.push({
                key: nextRowKey(),
                roleId: role,
                officialId: mo.officialId,
            });
        } else {
            rows.push({
                key: nextRowKey(),
                roleId: role,
            });
        }
    }

    for (const mo of pool) {
        rows.push({
            key: nextRowKey(),
            roleId: mo.roleId,
            officialId: mo.officialId,
        });
    }

    return rows;
}

interface Props {
    matchDetail: MatchDetailVm;
    onSaved: () => void | Promise<void>;
}

interface State {
    rows: OfficialPresetRow[];
    officials: OfficialVm[];
    loadingOfficials: boolean;
    saving: boolean;
}

class MatchOfficialsPresetForm extends React.PureComponent<Props, State> {
    public constructor(props: Props) {
        super(props);
        this.state = {
            rows: buildRowsFromMatch(props.matchDetail),
            officials: [],
            loadingOfficials: false,
            saving: false,
        };
    }

    public componentDidMount = (): void => {
        void this.loadOfficials();
    };

    public componentDidUpdate = (prevProps: Props): void => {
        if (prevProps.matchDetail.id !== this.props.matchDetail.id) {
            this.setState({ rows: buildRowsFromMatch(this.props.matchDetail) });
            void this.loadOfficials();
        }
    };

    private loadOfficials = async (): Promise<void> => {
        this.setState({ loadingOfficials: true });
        try {
            const list = await new OfficialsClient().getAll();
            this.setState({
                officials: list || [],
                loadingOfficials: false,
            });
        } catch {
            this.setState({ loadingOfficials: false });
        }
    };

    private addRow = (): void => {
        this.setState((s) => ({
            rows: [
                ...s.rows,
                {
                    key: nextRowKey(),
                    roleId: OfficialRoleEnum.Referee,
                },
            ],
        }));
    };

    private removeRow = (key: string): void => {
        this.setState((s) => ({
            rows: s.rows.filter((r) => r.key !== key),
        }));
    };

    private setRowOfficial = (key: string, officialId: number | undefined): void => {
        this.setState((s) => ({
            rows: s.rows.map((r) => (r.key === key ? { ...r, officialId } : r)),
        }));
    };

    private setRowRole = (key: string, roleId: OfficialRoleEnum): void => {
        this.setState((s) => ({
            rows: s.rows.map((r) => (r.key === key ? { ...r, roleId } : r)),
        }));
    };

    /** Official ids that appear in more than one row (same person cannot fill two slots). */
    private getDuplicateOfficialIds = (): number[] => {
        const { rows } = this.state;
        const counts = new Map<number, number>();
        for (const row of rows) {
            if (row.officialId !== undefined) {
                counts.set(row.officialId, (counts.get(row.officialId) || 0) + 1);
            }
        }
        const duplicateIds: number[] = [];
        counts.forEach((n, officialId): void => {
            if (n > 1) {
                duplicateIds.push(officialId);
            }
        });
        return duplicateIds;
    };

    private handleSave = async (): Promise<void> => {
        const { matchDetail, onSaved } = this.props;
        const { rows } = this.state;
        const mid = matchDetail.id;

        const pairs: { officialId: number; roleId: OfficialRoleEnum }[] = [];
        for (const row of rows) {
            if (row.officialId !== undefined && row.roleId !== undefined) {
                pairs.push({ officialId: row.officialId, roleId: row.roleId });
            }
        }

        if (pairs.length === 0) {
            errorWithUserMessage('Select at least one official with a role.');
            return;
        }

        const duplicateIds = this.getDuplicateOfficialIds();
        if (duplicateIds.length > 0) {
            const { officials } = this.state;
            const names = duplicateIds
                .map((id): string => {
                    const o = officials.find((x): boolean => x.id === id);
                    return o ? `${o.name} ${o.surname}` : `Official #${id}`;
                })
                .join(', ');
            errorWithUserMessage(
                `Each official can only be assigned once per match. Duplicates: ${names}.`
            );
            return;
        }

        const merged = pairs.map(
            (p) =>
                new CreateMatchOfficialCommandOfMatchOfficial({
                    id: 0,
                    matchId: mid,
                    officialId: p.officialId,
                    roleId: p.roleId,
                })
        );

        this.setState({ saving: true });
        try {
            const cmd = new CreateOrUpdateMatchOfficialsCommand({
                matchId: mid,
                matchOfficials: merged,
            });
            await new MatchesClient().updateOfficials(mid, cmd);
            success('Match officials saved.');
            await onSaved();
        } catch {
            errorWithUserMessage('Could not save officials.');
        } finally {
            this.setState({ saving: false });
        }
    };

    public render(): React.ReactElement {
        const { rows, officials, loadingOfficials, saving } = this.state;
        const roleOptions: DropdownOption[] = transformEnumToDropdownOptions(OfficialRoleEnum);

        const officialOptions = officials.map(
            (o: OfficialVm): { value: number; label: string } => ({
                value: o.id,
                label: `${o.name} ${o.surname}`,
            })
        );

        const duplicateOfficialIds = this.getDuplicateOfficialIds();
        const duplicateNames =
            duplicateOfficialIds.length > 0
                ? duplicateOfficialIds
                      .map((id): string => {
                          const o = officials.find((x): boolean => x.id === id);
                          return o ? `${o.name} ${o.surname}` : `Official #${id}`;
                      })
                      .join(', ')
                : '';

        return (
            <div className="match-officials-preset-form" style={{ padding: '0 4px' }}>
                <Text type="secondary">
                    Change role or official in each row, or add more below.
                </Text>
                {duplicateOfficialIds.length > 0 && (
                    <Alert
                        type="warning"
                        showIcon
                        style={{ marginTop: 12 }}
                        message="Same official selected more than once"
                        description={`Each person can only hold one assignment on this match. Change or clear duplicate rows for: ${duplicateNames}.`}
                    />
                )}
                <div style={{ marginTop: 12 }}>
                    {rows.map((row) => (
                        <div key={row.key} className="match-officials-preset-form__row">
                            <Space
                                style={{
                                    display: 'flex',
                                    width: '100%',
                                    flexWrap: 'wrap',
                                }}
                                align="center"
                                size={8}
                            >
                                <Select
                                    showSearch
                                    filterOption={filterOptionForSearch}
                                    optionFilterProp="label"
                                    placeholder="Official"
                                    style={{ minWidth: 220, flex: 1, maxWidth: '100%' }}
                                    loading={loadingOfficials}
                                    allowClear
                                    value={row.officialId}
                                    options={officialOptions}
                                    onChange={(v: number | undefined): void =>
                                        this.setRowOfficial(row.key, v)
                                    }
                                />
                                <Select
                                    placeholder="Role"
                                    style={{ minWidth: 168, width: 168 }}
                                    value={row.roleId}
                                    options={roleOptions.map((r) => ({
                                        value: parseInt(r.id, 10) as OfficialRoleEnum,
                                        label: r.name,
                                    }))}
                                    onChange={(v: OfficialRoleEnum): void =>
                                        this.setRowRole(row.key, v)
                                    }
                                />
                                <Button
                                    type="text"
                                    disabled={rows.length <= 1}
                                    icon={<DeleteOutlined />}
                                    aria-label="Remove row"
                                    onClick={(): void => this.removeRow(row.key)}
                                />
                            </Space>
                        </div>
                    ))}
                </div>
                <Button
                    type="dashed"
                    onClick={this.addRow}
                    icon={<PlusOutlined />}
                    style={{ marginTop: 4, marginBottom: 12 }}
                >
                    Add another official
                </Button>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                        type="primary"
                        loading={saving}
                        disabled={duplicateOfficialIds.length > 0}
                        onClick={(): void => void this.handleSave()}
                    >
                        Save officials
                    </Button>
                </div>
            </div>
        );
    }
}

export default MatchOfficialsPresetForm;
